import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Breadcrumb,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import ButtonField from "../../common/htmlComponents/ButtonField";
import {
  EditOutlined,
  FilterOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { debounce } from "lodash";
import { getRequest, postRequest } from "../../axios";
import {
  ErrorNotificationMsg,
  ShowErrorMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import { authUserData } from "../../utils/Helper";
import { ReactComponent as Archive } from "../../image/Archive.svg";
import { ReactComponent as Archiveicon } from "../../image/Archiveicon.svg";
import ComparativeScaleNew from "../../image/ComparativeScaleNew.png";
import Comparativ from "../../image/comparativ.png";

const getRecordRfqStatus = (record) =>
  String(record?.rfq_status ?? record?.status ?? "").trim();

const isComparativeStage = (record) => {
  const status = getRecordRfqStatus(record);
  return (
    status === "Offer Stage" ||
    status === "Negotiation Stage" ||
    status === "Comparison Approval Stage"
  );
};

const GeneralProcurementList = () => {
  const navigate = useNavigate();
  const formRef = useRef();

  const [clickedFilterButton, setClickedFilterButton] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [selectedRow, setSelectedRow] = useState([]);
  const [archiving, setArchiving] = useState(false);
  const [state, setState] = useState({
    page: 1,
    pagesize: 10,
    search: "",
    start_date: "",
    end_date: "",
  });

  const handleStartDateChange = (date) => {
    setStartDate(date ? moment(date).format("DD-MM-YYYY") : "");
  };

  const handleEndDateChange = (date) => {
    setEndDate(date ? moment(date).format("DD-MM-YYYY") : "");
  };

  const handleApplyFilter = () => {
    setClickedFilterButton(true);
    setState((prev) => ({
      ...prev,
      page: 1,
      start_date: startDate,
      end_date: endDate,
    }));
  };

  const handleClearFields = () => {
    setStartDate("");
    setEndDate("");
    formRef.current?.setFieldsValue({
      start_date: null,
      end_date: null,
    });
  };

  const handleReset = () => {
    setClickedFilterButton(false);
    setStartDate("");
    setEndDate("");
    setSearchInputValue("");
    setState({
      page: 1,
      pagesize: 10,
      search: "",
      start_date: "",
      end_date: "",
    });
    formRef.current?.resetFields();
  };

  const { Search } = Input;

  const applySearch = (value) => {
    setState((prev) => ({
      ...prev,
      page: 1,
      search: value || "",
    }));
  };

  const debouncedSearchRef = useRef(
    debounce((value) => applySearch(value), 600),
  );

  useEffect(
    () => () => {
      debouncedSearchRef.current?.cancel?.();
    },
    [],
  );

  const onSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearchRef.current?.(value);
  };

  const onSearchSubmit = (value) => {
    debouncedSearchRef.current?.cancel?.();
    setSearchInputValue(value || "");
    applySearch(value || "");
  };

  const fetchGeneralRfqs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: state.page,
        pagesize: state.pagesize,
      };
      if (state.search?.trim()) params.search = state.search.trim();
      if (state.start_date) params.start_date = state.start_date;
      if (state.end_date) params.end_date = state.end_date;

      const res = await getRequest("general-rfqs/", params);
      const payload = res?.data;

      const count =
        payload?.count ??
        payload?.results?.count ??
        payload?.results?.data?.length ??
        0;
      setTotalCount(count);

      const results = Array.isArray(payload?.results?.data)
        ? payload.results.data
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
      setTableData(results);
    } catch (err) {
      ShowErrorMsg(err);
      setTableData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    state.page,
    state.pagesize,
    state.search,
    state.start_date,
    state.end_date,
  ]);

  useEffect(() => {
    fetchGeneralRfqs();
  }, [fetchGeneralRfqs]);

  const handleEdit = useCallback(
    (record) => {
      const id = record?.id;
      if (id == null || id === "") {
        message.warning("Missing RFQ id for edit.");
        return;
      }
      navigate(`/general-procurement/edit/${id}`);
    },
    [navigate],
  );

  const handleArchive = useCallback(
    async (id = "") => {
      const ids = selectedRow.length > 0 ? [...selectedRow] : [];
      if (ids.length === 0 && id !== "" && id != null) {
        ids.push(id);
      }
      if (ids.length === 0) {
        message.warning("No RFQ selected to archive.");
        return;
      }

      try {
        setArchiving(true);
        const apiResponse = await postRequest("general-rfq-archive/", {
          deleted: ids,
        });
        const data = apiResponse?.data;
        const succeeded =
          data?.success === true ||
          data?.success === "true" ||
          (data?.success !== false && (apiResponse?.status ?? 200) < 400);
        if (succeeded) {
          const successText =
            (typeof data?.message === "string" && data.message) ||
            "RFQ archived successfully.";
          SuccessNotificationMsg(successText);
          setSelectedRow([]);
          fetchGeneralRfqs();
        } else {
          const failText =
            (typeof data?.message === "string" && data.message) ||
            "Failed to archive RFQ.";
          ErrorNotificationMsg(failText);
        }
      } catch (err) {
        ShowErrorMsg(err);
      } finally {
        setArchiving(false);
      }
    },
    [fetchGeneralRfqs, selectedRow],
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedRow,
      onChange: (selectedRowKeys) => setSelectedRow(selectedRowKeys),
    }),
    [selectedRow],
  );

  const renderRfqType = (v) => {
    const k = String(v ?? "").toLowerCase();
    if (k === "quick") return "Quick RFQ";
    if (k === "general") return "General RFQ";
    return v ?? "—";
  };

  const formatDate = (raw) => {
    if (!raw) return "—";
    const m = moment(raw, ["DD-MM-YYYY", "DD-MM-YY", "YYYY-MM-DD"], true);
    const parsed = m.isValid() ? m : moment(raw);
    return parsed.isValid() ? parsed.format("DD-MM-YYYY") : String(raw);
  };

  const formatDateTime = (raw) => {
    if (!raw) return "—";
    const m = moment(raw, ["DD-MM-YYYY HH:mm", "DD-MM-YY HH:mm"], true);
    const parsed = m.isValid() ? m : moment(raw);
    return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm") : String(raw);
  };

  const formatAmount = (raw, currency) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return "—";
    const symbol = currency === "INR" ? "₹" : currency ? `${currency} ` : "";
    return `${symbol}${n.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })}`;
  };

  const renderRfqStatus = (status) => {
    if (!status) return "—";
    const s = String(status).toLowerCase();
    let color = "default";
    if (s.includes("draft")) color = "default";
    else if (s.includes("under approval") || s.includes("pending"))
      color = "processing";
    else if (s.includes("sent") || s.includes("open")) color = "processing";
    else if (s.includes("approve")) color = "success";
    else if (s.includes("reject") || s.includes("cancel")) color = "error";
    else if (s.includes("close")) color = "warning";
    return <Tag color={color}>{String(status)}</Tag>;
  };

  const itemColumns = useMemo(
    () => [
      {
        title: "Sr.",
        key: "sr",
        // width: 60,
        render: (_, __, idx) => idx + 1,
      },
      {
        title: "Item Description",
        dataIndex: "item_description",
        key: "item_description",
        render: (v) => v ?? "—",
      },
      // {
      //   title: "Approved Make",
      //   dataIndex: "approved_make",
      //   key: "approved_make",
      //   width: 180,
      //   render: (v) => v ?? "—",
      // },
      {
        title: "Qty.",
        dataIndex: "quantity",
        key: "quantity",
        // width: 110,
        align: "right",
        render: (v) => {
          const n = Number(v);
          return Number.isFinite(n)
            ? n.toLocaleString("en-IN", { maximumFractionDigits: 3 })
            : v ?? "—";
        },
      },
      {
        title: "UoM",
        dataIndex: "unit_of_measurement",
        key: "unit_of_measurement",
        // width: 110,
        render: (v) => v ?? "—",
      },
      {
        title: "Unit Rate",
        dataIndex: "unit_rate",
        key: "unit_rate",
        // width: 130,
        align: "right",
        render: (v) => {
          const n = Number(v);
          return Number.isFinite(n)
            ? n.toLocaleString("en-IN", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })
            : v ?? "—";
        },
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        // width: 140,
        align: "right",
        render: (v, record, _idx) => {
          const n = Number(v);
          const currency = record?.__parentCurrency;
          if (!Number.isFinite(n)) return v ?? "—";
          const symbol =
            currency === "INR" ? "₹" : currency ? `${currency} ` : "";
          return `${symbol}${n.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}`;
        },
      },
      {
        title: "Remarks from Buyer",
        dataIndex: "remarks_from_buyer",
        key: "remarks_from_buyer",
        // width: 340,

        render: (v) => v ?? "—",
      },
    ],
    [],
  );

  const renderExpandedItems = (record) => {
    const items = Array.isArray(record?.items) ? record.items : [];
    if (!items.length) {
      return (
        <div style={{ padding: "8px 12px", color: "#6B7280" }}>
          No items in this RFQ.
        </div>
      );
    }
    const dataWithCurrency = items.map((it) => ({
      ...it,
      __parentCurrency: record?.currency,
    }));
    return (
      <Table
        size="small"
        columns={itemColumns}
        dataSource={dataWithCurrency}
        pagination={false}
        rowKey={(it, idx) => it?.id ?? `${record?.id ?? "row"}-${idx}`}
        // scroll={{ x: 900 }}
        style={{ margin: "8px 0" }}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        title: "RFQ Number",
        dataIndex: "rfq_number",
        key: "rfq_number",
        width: 120,
        render: (v) => v ?? "—",
      },
      {
        title: "RFQ Type",
        dataIndex: "rfq_type",
        key: "rfq_type",
        width: 130,
        render: renderRfqType,
      },
      {
        title: "Company",
        dataIndex: "company_name",
        key: "company_name",
        render: (v) => v ?? "—",
      },
      {
        title: "Created By",
        dataIndex: "created_by_name",
        key: "created_by_name",
        width: 140,
        render: (v) => v ?? "—",
      },
      {
        title: "Created Date",
        dataIndex: "created_at",
        key: "created_at",
        width: 130,
        render: (v, record) =>
          formatDate(v ?? record?.rfq_created_date ?? null),
      },
      {
        title: "Bid Due Date & Time",
        dataIndex: "rfq_bid_due_date_time",
        key: "rfq_bid_due_date_time",
        width: 170,
        render: (v) => formatDateTime(v),
      },
      {
        title: "Delivery Date",
        dataIndex: "delivery_date",
        key: "delivery_date",
        width: 130,
        render: (v) => formatDate(v),
      },
      // {
      //   title: "Items",
      //   dataIndex: "items",
      //   key: "items",
      //   width: 90,
      //   align: "center",
      //   render: (v) => (Array.isArray(v) ? v.length : 0),
      // },
      {
        title: "Total Amount",
        dataIndex: "total_amount",
        key: "total_amount",
        width: 150,
        align: "right",
        render: (v, record) => formatAmount(v, record?.currency),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        // width: 180,
        render: (v, record) => renderRfqStatus(v ?? record?.rfq_status ?? null),
      },
      {
        title: "Action",
        key: "operation",
        fixed: "right",
        width: 160,
        render: (_, record) => (
          <Space size="small">
            {/* <Tooltip title="Edit" color="#094E8F">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined style={{ color: "#094E8F" }} />}
                onClick={() => handleEdit(record)}
                aria-label="Edit RFQ"
              />
            </Tooltip> */}
            {
              authUserData()?.permission?.includes("rfq|add_floatrfq") &&
              //isComparativeStage(record) &&
              record.status !== "RFQ Floated" &&
              record.status !== "RFQ under Approval" &&

              record.purchase_order === false && (
              <Tooltip title="Comparatives" placement="left" color="#094E8F">
                <Link to={`/general-procurement/comparative/${record.id}`}>
                  <img
                    style={{ width: 22, display: "block" }}
                    src={ComparativeScaleNew}
                    alt="Comparatives"
                  />
                </Link>
              </Tooltip>
            )
            }
            {authUserData()?.group_permission?.includes("rfq|add_floatrfq") &&
              record.purchase_order_approved === true && (
              <Tooltip title="View Comparatives" placement="left" color="#094E8F">
                <Link to={`/general-procurement/comparative/${record.id}`}>
                  <img
                    style={{ width: 22, display: "block" }}
                    src={Comparativ}
                    alt="View Comparatives"
                  />
                </Link>
              </Tooltip>
            )}
            <Popconfirm
              className="action"
              title="Are you sure you want to archive this RFQ?"
              okText="Yes"
              cancelText="No"
              placement="left"
              onConfirm={() => handleArchive(record.id)}
            >
              <Tooltip title="Archive" placement="left" color="#094E8F">
                <Archive style={{ cursor: "pointer" }} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleEdit, handleArchive],
  );

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>Quick / General RFQ — History</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Dashboard</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Quick / General RFQ — History</Breadcrumb.Item>
            </Breadcrumb>
          </div>

          <Space>
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              htmlType="button"
              label="Create RFQ"
              onClick={() => navigate("/general-procurement/add")}
            />
            <Tooltip placement="topLeft" title="Archived Records">
              <Link
                to="/general-procurement/history/archive"
                className="Archiveicon"
              >
                <Archiveicon />
              </Link>
            </Tooltip>
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <div className="filterrow">
            <div className="filtercol">
              <Form autoComplete="off" ref={formRef}>
                <Space wrap>
                  <Col xs={24} className="TextFeild gutter-row">
                    <Form.Item name="start_date">
                      <DatePicker
                        format="DD-MM-YYYY"
                        placeholder="RFQ created from"
                        onChange={handleStartDateChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} className="TextFeild gutter-row">
                    <Form.Item name="end_date">
                      <DatePicker
                        format="DD-MM-YYYY"
                        placeholder="RFQ created to"
                        onChange={handleEndDateChange}
                      />
                    </Form.Item>
                  </Col>

                  <Button
                    type="primary"
                    htmlType="button"
                    icon={
                      <Tooltip title="Apply" color="#094E8F">
                        <FilterOutlined style={{ fontSize: "20px" }} />
                      </Tooltip>
                    }
                    style={{
                      backgroundColor: "#094e8f",
                      color: "#ffffff",
                      border: "none",
                      width: "40px",
                      height: "40px",
                      boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                      borderRadius: "4px",
                    }}
                    onClick={startDate || endDate ? handleApplyFilter : null}
                  />
                  <Button
                    type="primary"
                    htmlType="button"
                    icon={
                      <Tooltip title="Reset" color="#094E8F">
                        <UndoOutlined style={{ fontSize: "20px" }} />
                      </Tooltip>
                    }
                    style={{
                      backgroundColor: "#094e8f",
                      color: "#ffffff",
                      border: "none",
                      width: "40px",
                      height: "40px",
                      boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                      borderRadius: "4px",
                    }}
                    onClick={
                      clickedFilterButton ? handleReset : handleClearFields
                    }
                  />
                </Space>
              </Form>
            </div>

            <div className="btnscol">
              <Space>
                <div className="searchwrp">
                  <Space direction="vertical">
                    <Search
                      placeholder="Search"
                      onSearch={onSearchSubmit}
                      allowClear
                      style={{ width: 250 }}
                      value={searchInputValue}
                      onChange={onSearchInputChange}
                    />
                  </Space>
                </div>
              </Space>
              {selectedRow.length > 0 && (
                <Popconfirm
                  className="action"
                  title="Are you sure you want to archive the selected RFQs?"
                  okText="Yes"
                  cancelText="No"
                  placement="left"
                  onConfirm={() => handleArchive()}
                >
                  <ButtonField
                    className="BlueShadowBtn"
                    type="primary"
                    htmlType="button"
                    label={`Archive (${selectedRow.length})`}
                    loading={archiving}
                  />
                </Popconfirm>
              )}
            </div>
          </div>

          <div className="tablewrp">
            <Table
              scroll={{ x: 1500 }}
              columns={columns}
              dataSource={tableData}
              loading={loading}
              rowSelection={rowSelection}
              rowKey={(record) =>
                record?.id ??
                record?.rfq_number ??
                `${record?.created_at ?? ""}-${record?.created_by ?? ""}`
              }
              pagination={{
                current: state.page,
                pageSize: state.pagesize,
                total: totalCount,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                locale: { items_per_page: "Items / Page" },
                showTotal: (total) => `Total ${total} items`,
              }}
              onChange={(pagination) => {
                setState((prev) => ({
                  ...prev,
                  page: pagination.current,
                  pagesize: pagination.pageSize,
                }));
              }}
              expandable={{
                expandedRowRender: renderExpandedItems,
                rowExpandable: (record) =>
                  Array.isArray(record?.items) && record.items.length > 0,
              }}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default GeneralProcurementList;
